import {
  EventType,
  type Configuration,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import {
  getRuntimeConfig,
  getRuntimeConfigValidationMessage,
  validateRuntimeConfig,
} from "./runtimeConfig/runtime-config";

type ReadyAuthBootstrap = {
  status: "ready";
  instance: PublicClientApplication;
  loginRequest: {
    scopes: string[];
    prompt?: "select_account" | "login";
  };
  backendRequestScope: {
    scopes: string[];
  };
};

type InvalidAuthBootstrap = {
  status: "invalid";
  message: string;
};

export type AuthBootstrapResult = ReadyAuthBootstrap | InvalidAuthBootstrap;

const TENANT_AUTHORITY =
  "https://login.microsoftonline.com/7a7dc48e-9f70-4f4c-941e-bf8aa042f81a";

const getUniqueScopes = (...scopes: string[]) =>
  Array.from(new Set(scopes.filter(Boolean)));

export let msalInstance: PublicClientApplication | undefined;
export let backendRequestScope: { scopes: string[] } | undefined;

export const createAuthBootstrap = (): AuthBootstrapResult => {
  const validation = validateRuntimeConfig();

  if (!validation.isValid) {
    return {
      status: "invalid",
      message: getRuntimeConfigValidationMessage(validation),
    };
  }

  const runtimeConfig = getRuntimeConfig();

  const msalConfig: Configuration = {
    auth: {
      clientId: runtimeConfig.MSAL_PUBLIC_CLIENT_ID,
      authority: TENANT_AUTHORITY,
      redirectUri: "/",
      postLogoutRedirectUri: "/",
    },
    cache: {
      cacheLocation: "sessionStorage",
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }

          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              return;
          }
        },
      },
    },
  };

  const instance = new PublicClientApplication(msalConfig);
  const backendScope = {
    scopes: [runtimeConfig.MSAL_PUBLIC_BACKEND_SCOPE],
  };

  instance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event.payload &&
      "account" in event.payload
    ) {
      const account = event.payload.account;
      if (account) {
        instance.setActiveAccount(account);
      }
    }
  });

  msalInstance = instance;
  backendRequestScope = backendScope;

  return {
    status: "ready",
    instance,
    loginRequest: {
      scopes: getUniqueScopes(
        "openid",
        "profile",
        runtimeConfig.MSAL_PUBLIC_AUTH_SCOPE,
      ),
      prompt: "select_account",
    },
    backendRequestScope: backendScope,
  };
};
